<template>
	<div class="dynamic-data-mapper pr-2" :class="{ 'show-scroller': fieldMapping.length > 2 }">
		<div class="map-title mt-2">1. Choose your {{ serviceName }} {{ fieldItem }}</div>
		<div class="map-description">Which {{ serviceName }} {{ fieldItem }} do you want to link this feature to?</div>
		<DynamicParamsDispatcher
			:interfaceFieldUUID="interfaceFieldUUID"
			:action="{ mapped_resource: this.fieldItem }"
			:service="service"
			:params="params"
			:dynamicNode="dynamicNode"
			@send-params-map="onParamsMapChange"
			@send-params-map-labels="onParamsMapLabelsChange"
		/>
		<div class="loader-container mt-3 mb-n3">
			<AppLoader size="18px" v-if="loading" />
		</div>
		<div class="map-your-data-container" v-if="fieldMapping.length > 0">
			<div class="map-title mt-6">2. Map your data</div>
			<div class="map-description">Choose the corresponding field in your {{ serviceName }} {{ fieldItem }}</div>
			<div class="mapping-selection display-flex flex-column">
				<SingleMapping
					v-for="map in fieldMapping"
					:key="map.originalKey"
					class="my-1"
					:value="specificMapResponse(map.originalKey)"
					:source="specificMapResponse(map.originalKey)"
					:items="mappingOptions"
					:serviceSlug="serviceSlug"
					@onSingleMappingChanged="(dest) => updateMapping(map, dest)"
				>
					<div class="input-style">{{ map.displayName }}</div>
				</SingleMapping>
			</div>
		</div>
	</div>
</template>

<script>
import Vue from 'vue';

import { mapGetters, mapActions } from 'vuex';
import { editedWorkflowVersion, editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { getConfigurationAndInterfaceIdByFieldUUID } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { getSourceForMappingsInMapper } from '@/store/modules/selectedWorkflow/modules/userSchemaMapper/actions';
import { getWidget } from '@/store/modules/widgets/getters';
import { sourceForMappings } from '@/store/modules/selectedWorkflow/modules/userSchemaMapper/getters';
import { configurationsValuesByFieldUUID } from '@/store/modules/configurationEditor/getters';

import ParamsService from '@/utils/ParamsService';

import DynamicParamsDispatcher from './DynamicParamsDispatcher';
import SingleMapping from './SingleMapping';
import { isNullOrUndefinedOrEmpty } from '@/utils/helpers';

export default {
	name: 'DynamicDataMapping',
	props: {
		interfaceFieldUUID: { type: String },
		fieldItem: { type: String },
		service: { type: Object },
		response: { type: Object },
	},
	components: {
		DynamicParamsDispatcher,
		SingleMapping,
	},
	computed: {
		...mapGetters([editWorkflow.fullPath, editedWorkflowVersion.fullPath, sourceForMappings.fullPath]), // tdo: remove this and all Vuesx access
		...mapGetters([getConfigurationAndInterfaceIdByFieldUUID.fullPath, getWidget.fullPath, configurationsValuesByFieldUUID.fullPath]),
		configurationAndInterfaceIdByFieldUUID() {
			return this[getConfigurationAndInterfaceIdByFieldUUID.fullPath](this.interfaceFieldUUID);
		},
		interfaceUUID() {
			return this.configurationAndInterfaceIdByFieldUUID.interfaceUUID;
		},
		interface() {
			return this[getWidget.fullPath](this.interfaceUUID);
		},
		mappingField() {
			return this.interface?.fields.find((x) => x.id === this.interfaceFieldUUID) || {};
		},
		params() {
			return ParamsService.getParamsForMappingByItem(this.service, this.fieldItem);
		},
		sourceActionString() {
			return ParamsService.getParamsSourceForMapping(this.service, this.fieldItem);
		},
		workflowVersion() {
			return this[editedWorkflowVersion.fullPath];
		},
		workflowId() {
			return this[editWorkflow.fullPath]?.id;
		},
		serviceSlug() {
			return this.service?.service_slug;
		},
		serviceName() {
			return this.service.service_name;
		},
		mappingOptions() {
			return this[sourceForMappings.fullPath](this.interfaceFieldUUID) || [];
		},
		fieldMapping() {
			return this.mappingField?.metadata?.source?.mappings || [];
		},
		mappingKeys() {
			return this.fieldMapping.map((m) => m.originalKey);
		},
		creatorMapResponse() {
			return this[configurationsValuesByFieldUUID.fullPath][this.interfaceFieldUUID] || {};
		},
		creatorMapResponseParamsMap() {
			return this.creatorMapResponse?.params_map || {};
		},
		creatorMapResponseParamsMapLabels() {
			return this.creatorMapResponse?.params_map_labels || {};
		},
		creatorMapResponseMappings() {
			return this.creatorMapResponse?.mappings || [];
		},
		creatorMapResponseMappingsKeys() {
			return this.creatorMapResponse?.mappings?.map((x) => x.originalKey) || [];
		},
		dynamicNode() {
			return {
				params_map: this.localParamsMapResponse,
				params_map_labels: this.localParamsMapLabelsResponse,
				mappings: this.localMappingsResponse,
				user_schema_mappings_source_field_uuid: this.interfaceFieldUUID,
				credentials_configuration: this.mappingField?.metadata?.source?.credentials_configuration,
			};
		},
	},
	methods: {
		...mapActions([getSourceForMappingsInMapper.fullPath]),
		updateMapping(map, dest) {
			const responseMap = { ...map, userKey: dest };
			const newMappings = [...this.localMappingsResponse.filter((x) => x.originalKey !== map.originalKey), responseMap];
			this.$set(this, 'localMappingsResponse', newMappings);
			this.$emit('on-mapping-updated', newMappings);
		},
		onDefaultDataSourceChange(src, newSrc) {
			Vue.set(this.defaultDataMapping, newSrc, this.defaultDataMapping[src]);
			this.deleteDefaultDataMapping(src);
		},
		addDefaultDataMapping() {
			Vue.set(this.defaultDataMapping, '', '');
		},
		deleteDefaultDataMapping(src) {
			Vue.delete(this.defaultDataMapping, src);
		},
		async fetchOptionsToStore() {
			const paramsMapCount = Object.keys(this.localParamsMapResponse).length;
			if (paramsMapCount === 0) {
				return;
			}
			const requiredParams = this.params.filter((param) => param.required);
			const shouldFetch = requiredParams.every((param) => !isNullOrUndefinedOrEmpty(this.localParamsMapResponse[param.key]));
			if (!shouldFetch) {
				console.log(`not all params are filled`);
				return;
			}
			this.loading = true;
			await this[getSourceForMappingsInMapper.fullPath]({
				widgetFieldUUID: this.interfaceFieldUUID,
				sourceActionString: this.sourceActionString,
				searchString: this.sourceActionString,
				workflowVersionId: this[editedWorkflowVersion.fullPath]?.id,
				overrideParamsMap: this.localParamsMapResponse,
			})
				.catch((err) => {
					console.error(err);
					this.loading = false;
				})
				.finally(() => {
					this.loading = false;
				});
		},
		onParamsMapChange(paramsMap) {
			this.$set(this, 'localParamsMapResponse', paramsMap);
			this.$emit('on-params-map-updated', paramsMap);
			this.fetchOptionsToStore();
		},
		onParamsMapLabelsChange(paramsMapLabels) {
			this.$set(this, 'localParamsMapLabelsResponse', paramsMapLabels);
			this.$emit('on-params-map-labels-updated', paramsMapLabels);
		},
		specificMapResponse(src) {
			return this.creatorMapResponseMappings.find((x) => x.originalKey === src)?.userKey;
		},
	},
	async mounted() {
		this.localMappingsResponse = this.creatorMapResponseMappings;
		this.localParamsMapResponse = this.creatorMapResponseParamsMap;
		this.localParamsMapLabelsResponse = this.creatorMapResponseParamsMapLabels;
		this.fetchOptionsToStore();
	},
	data() {
		return {
			loading: false,
			defaultDataMapping: {},
			localMappingsResponse: [],
			allPossibleFields: null,
			localParamsMapResponse: {},
			localParamsMapLabelsResponse: {},
		};
	},
};
</script>

<style lang="scss" scoped>
.show-scroller::-webkit-scrollbar {
	width: 7px;
	-webkit-appearance: none;
}

.show-scroller::-webkit-scrollbar-thumb {
	background: rgb(173, 164, 164);
	border-radius: 6px;
}

.dynamic-data-mapper {
	height: 400px;
	overflow-y: auto; // this is a bit of a hack - should be handled by AppDialog

	.mapping-selection {
		::v-deep(.v-input) {
			margin: 0 !important;
			padding: 0 !important;
		}
		.input-style {
			padding: 0 12px;
		}
	}
	.tertiary-link-style {
		color: #000000 !important;
	}

	.map-title {
		font-size: 16px;
		font-weight: 600;
	}
	.map-description {
		font-size: 12px;
		color: var(--secondary-font-color);
	}
}
.loader-container {
	height: 20px;
}
</style>
